import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  BookOpen, 
  Layout, 
  FileSpreadsheet, 
  ClipboardList,
  GraduationCap,
  Image,
  LogOut,
  Upload,
  AlertCircle,
  Loader2,
  FolderOpen,
  Menu,
  X,
  Home
} from 'lucide-react';

const navItems = [
  { to: '/', icon: Home, label: 'Inicio' },
  { to: '/management', icon: ClipboardList, label: 'Documentos de Gestión' },
  { to: '/projects', icon: Layout, label: 'Proyectos' },
  { to: '/units', icon: BookOpen, label: 'Unidades' },
  { to: '/sessions', icon: FileSpreadsheet, label: 'Sesiones' },
  { to: '/evaluation', icon: GraduationCap, label: 'Evaluación' },
  { to: '/evidence', icon: Image, label: 'Evidencias' },
  { to: '/my-files', icon: FolderOpen, label: 'Mis Archivos' },
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function Navigation() {
  const [curriculumFile, setCurriculumFile] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setIsUploading(true);

    try {
      if (file.type !== 'application/pdf') {
        throw new Error('Por favor, suba un archivo PDF');
      }

      if (file.size > MAX_FILE_SIZE) {
        throw new Error('El archivo es demasiado grande. El tamaño máximo es 50MB');
      }

      // In development mode, just store file info locally
      const fileData = {
        name: file.name,
        url: URL.createObjectURL(file),
        size: file.size,
        type: file.type
      };

      setCurriculumFile(fileData.name);
      localStorage.setItem('curriculumFile', JSON.stringify(fileData));
      setUploadError(null);

    } catch (error) {
      console.error('Error al subir el archivo:', error);
      if (error instanceof Error) {
        setUploadError(error.message);
      } else {
        setUploadError('Error desconocido al subir el archivo');
      }
    } finally {
      setIsUploading(false);
      const input = document.getElementById('curriculum-upload') as HTMLInputElement;
      if (input) input.value = '';
    }
  };

  const handleChangeFile = () => {
    if (window.confirm('¿Desea cargar un nuevo Diseño Curricular?')) {
      document.getElementById('curriculum-upload')?.click();
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  React.useEffect(() => {
    const savedFile = localStorage.getItem('curriculumFile');
    if (savedFile) {
      try {
        const fileData = JSON.parse(savedFile);
        setCurriculumFile(fileData.name);
      } catch (e) {
        console.error('Error parsing saved curriculum file data:', e);
        localStorage.removeItem('curriculumFile');
      }
    }
  }, []);

  return (
    <>
      {/* Sidebar para pantallas medianas y grandes */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-30">
        <div className="p-4 border-b">
          <Link to="/" className="flex items-center space-x-2">
            <BookOpen className="w-8 h-8 text-indigo-600" />
            <span className="text-xl font-bold text-gray-900">PROFE JHONY</span>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200
                ${isActive
                  ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t">
          {uploadError && (
            <div className="mb-4 text-red-600 text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="break-words">{uploadError}</span>
            </div>
          )}
          
          {curriculumFile ? (
            <button
              onClick={handleChangeFile}
              disabled={isUploading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-indigo-700 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors duration-200"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              ) : (
                <Upload className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="truncate">{curriculumFile}</span>
            </button>
          ) : (
            <label className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-200">
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span>Cargar DCN</span>
              <input
                id="curriculum-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          )}

          <button
            onClick={handleSignOut}
            className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Overlay para menú móvil */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-gray-900 bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Menú móvil */}
      <div className={`md:hidden fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform transition-transform duration-300 z-50 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 border-b flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2" onClick={() => setIsMobileMenuOpen(false)}>
            <BookOpen className="w-8 h-8 text-indigo-600" />
            <span className="text-xl font-bold text-gray-900">PROFE JHONY</span>
          </Link>
          <button 
            onClick={() => setIsMobileMenuOpen(false)} 
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200
                ${isActive
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Botón de menú móvil */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="md:hidden fixed top-4 left-4 z-30 p-2 rounded-lg bg-white shadow-lg hover:bg-gray-50"
      >
        <Menu className="w-6 h-6 text-gray-600" />
      </button>
    </>
  );
}